import { css, useTheme } from "@emotion/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

// import kebabCase from "lodash/fp/kebabCase";
import Clickable from "./Clickable.jsx";
// import clsx from "clsx";
import DropdownNav from "./DropdownNav.jsx";
import findTreeItems from "./findTreeItems.js";
import flattenTree from "./flattenTree.js";
import handleComponentsProp from "./handleComponentsProp.js";
import Heading from "./Heading.jsx";
import MaybeFragment from "./MaybeFragment.jsx";
import useThemeProps from "./useThemeProps.jsx";

const outlineNavContext = createContext();

// function OutlineNavList({ item, ...restProps }) {
//   if (!item.children?.length) {
//     return null;
//   }
//   return (
//     <ol
//       css={css`
//         list-style: none;
//         padding: 0;
//       `}
//       {...restProps}
//     >
//       {item.children.map((item, index) => {
//         return <OutlineNavListItem key={index} item={item} />;
//       })}
//     </ol>
//   );
// }

function OutlineNavListItem(props) {
  // eslint-disable-next-line no-unused-vars
  const theme = useTheme();
  props = useThemeProps({ props, name: "OutlineNavListItem" });
  let { item, hoverColor = "gray.50" } = props;
  let [isIntersecting, setIsIntersecting] = useState(false);

  let { addCurrentItem, removeCurrentItem, lastCurrentItem } =
    useContext(outlineNavContext);

  useEffect(() => {
    let observer = new IntersectionObserver(
      (entries) => {
        let [entry] = entries;
        setIsIntersecting(entry.isIntersecting);
      },
      {
        rootMargin: "99999px 0px -50% 0px",
      },
    );
    observer.observe(item.element);
    return () => observer.disconnect();
  }, [item.element]);

  useEffect(() => {
    if (isIntersecting) {
      addCurrentItem(item.id);
    } else {
      removeCurrentItem(item.id);
    }
  }, [isIntersecting]);

  return (
    <Clickable
      href={`#${item.id}`}
      css={css`
        font-weight: ${lastCurrentItem?.id === item.id ? "bold" : "normal"};
        color: inherit;
        display: block;
        width: 100%;
        background: ${theme.getColor("white")};
        padding: 0.5rem;
        box-sizing: border-box;
        padding-block: ${theme.getLength([4, 4])};
        padding-inline: ${theme.getLength([4, 6])};
        text-decoration: none;
      `}
      hoverStyle={{
        backgroundColor: theme.getColor(hoverColor),
      }}
      onClick={(event) => {
        event.preventDefault();
        item.element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        // collapseOwner();
      }}
    >
      {item.text}
    </Clickable>
    /* <OutlineNavList
        item={item}
        css={css`
          padding-left: 1rem;
        `}
      /> */
  );
}

function makeItems(headings) {
  let previousHeading;
  let items = headings
    .map((heading) => {
      // if (!heading.id) {
      //   let count = 0;
      //   let makeId = () =>
      //     `${kebabCase(heading.textContent)}${
      //       count++ > 0 ? `-${count - 1}` : ""
      //     }`;
      //   let id;
      //   do {
      //     id = makeId();
      //   } while (document.getElementById(id));
      //   heading.id = id;
      // }
      let item = {
        id: heading.id,
        text: heading.textContent,
        level: previousHeading
          ? Math.min(
              parseInt(heading.tagName.slice(1)),
              previousHeading.level + 1,
            )
          : parseInt(heading.tagName.slice(1)),
        children: [],
        element: heading,
      };
      previousHeading = item;
      return item;
    })
    .filter((item) => item.level > 1);
  items.forEach((item, index, items) => {
    let children = items.slice(index + 1);
    let endAt = children.findIndex(
      (otherItem) => otherItem.level <= item.level,
    );
    if (endAt === -1) {
      endAt = undefined;
    }
    children = children
      .slice(0, endAt)
      .filter((child) => child.level === item.level + 1);
    item.children = children;
  });
  items = items.filter(
    (item, index, items) =>
      item.level === Math.min(...items.map((item) => item.level)),
  );
  return items;
}

export default function OutlineNav(props) {
  // eslint-disable-next-line no-unused-vars
  const theme = useTheme();
  props = useThemeProps({ props, name: "OutlineNav" });
  let { source, wrapperProps, components, ...restProps } = props;
  components = handleComponentsProp(components, { Wrapper: MaybeFragment });
  let { Wrapper } = components;

  let [items, setItems] = useState([]);

  let [currentItemIds, setCurrentItemIds] = useState([]);

  let currentItems = findTreeItems(
    items,
    (item) => currentItemIds.includes(item.id),
    { childrenKey: "children" },
  );

  let firstCurrentItem = currentItems[0];
  let lastCurrentItem = currentItems[currentItems.length - 1];

  const update = useCallback(() => {
    let headings = source
      ? [...source.querySelectorAll("[id]:is(h1, h2)")]
      : [];
    setItems(makeItems(headings));
  }, [source]);

  useEffect(() => {
    const observer = new MutationObserver((/*records, observer*/) => {
      update();
    });
    update();
    if (source) {
      observer.observe(source, {
        subtree: true,
        childList: true,
        attributeFilter: ["id"],
      });
      return () => observer.disconnect();
    }
  }, [source]);

  const addCurrentItem = useCallback((id) => {
    setCurrentItemIds((currentItemIds) => [...currentItemIds, id]);
  }, []);

  const removeCurrentItem = useCallback((id) => {
    setCurrentItemIds((currentItemIds) =>
      currentItemIds.filter((currentItem) => currentItem !== id),
    );
  }, []);

  // const collapseOwner = useCallback(() => {
  //   setIsExpanded(false);
  // }, []);

  if (!items?.length) {
    return null;
  }

  let flattenedItems = flattenTree(items, { childrenKey: "children" });

  return (
    <outlineNavContext.Provider
      value={{
        addCurrentItem,
        removeCurrentItem,
        currentItemIds,
        currentItems,
        lastCurrentItem,
        firstCurrentItem,
        // collapseOwner,
      }}
    >
      <Wrapper {...wrapperProps}>
        <DropdownNav
          items={flattenedItems}
          label={<Heading variant="h4">{"On this page"}</Heading>}
          components={{
            Item: OutlineNavListItem,
          }}
          {...restProps}
        />
      </Wrapper>
    </outlineNavContext.Provider>
  );
}
